import React from "react";
import {
  SiteNavigationElementSchema,
  LocalBusinessSchema,
  RecaptchaProvider,
  TimedElement,
  FishermanModal,
  FishermanIcon,
  Announcement,
  ScrollElement,
  NavigationV2,
  PopupAccordionMenuLink,
  FishermanHeader,
  HookComponentProvider,
  Phone,
  LocationsContext,
  SiteMap,
  SocialMediaV2,
  MultipleComponentIterator,
  Location,
  Email,
  HoursOfOperation,
  CopyrightLabel,
  OutboundLink,
  FishermanBanner,
  PageTransition,
} from "@bluefin/components";
import { Button, Grid } from "semantic-ui-react";
import { useContext } from "react";
import { graphql, StaticQuery } from "gatsby";

import {
  getComponentFiles,
  createImages,
  joinParentWithChildPages,
  filterRegularLinks,
  filterButtonLinks,
  getParentGeneratedChildPages,
} from "../utils/utils";
import "../semantic/semantic.less";
import "../style/WebsiteLayout.css";

export default ({ children }) => (
  <StaticQuery
    query={graphql`
      query {
        allFishermanBusinessWebsitePage(
          sort: { order: ASC, fields: navigationPriority }
        ) {
          nodes {
            slug
            description
            url
            pageType
            title
            navigationPriority
            groupName
            props
            _id
          }
        }
        fishermanBusiness {
          name
          logo
          menu {
            schedules {
              availableDays
              categories
              _id
              name
            }
            categories {
              items
              name
              _id
            }
            items {
              modifierSets
              description
              _id
              name
              variations {
                _id
                menuItem
                name
                order
                price
              }
            }
            modifierSets {
              _id
              minAllowed
              modifiers
              name
            }
            modifiers {
              _id
              name
              price
            }
          }
          primaryLocation {
            timezone
            _id
          }
          _id
          type
          gatsbyLogo {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, height: 80)
            }
          }
          socialMedia {
            _id
            link
            type
          }
          brandIdentity
        }
        allFishermanBusinessCategory {
          nodes {
            category
            subCategory
          }
        }
        allFishermanBusinessLocation {
          nodes {
            street
            city
            state
            zipCode
            country
            email
            phoneNumber
            name
            hours {
              open
              close
              day
            }
          }
        }
        fishermanBusinessWebsitePage(pageType: { eq: "Home" }) {
          components {
            fastId
            componentIdentifier
            order
            files {
              file
            }
          }
        }
        announcementComponent: fishermanWebsiteComponent(
          layout: { name: { eq: "@" } }
          fastId: { eq: "Announcement" }
        ) {
          data {
            delay
            referenceId
            imagePosition
          }
          files {
            _id
            altText
            file
            gatsbyFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
            }
          }
          contentNodes {
            _id
            content
            contentType
          }
          interactions {
            _id
            order
            behaviorType
            displayType
            enabled
            label
            style
            url
          }
        }
        allSitePage {
          nodes {
            path
            context {
              title
              fishermanParentPageId
              tagName
              fishermanParentPageType
              navigationOrder
              navigationGroup
              navigationGroupOrder
            }
          }
        }
        footerRegularPages: allFishermanBusinessWebsitePage(
          filter: { pageType: { ne: "Services" } }
        ) {
          nodes {
            _id
            title
            slug
            pageType
            url
            navigationPriority
            props
          }
        }
        bookingUrl: fishermanBusinessWebsitePage(
          title: { regex: "/Book Now|Make An Appointment/" }
          pageType: { eq: "External" }
        ) {
          url
        }
      }
    `}
    render={(data) => {
      const {
        allFishermanBusinessWebsitePage,
        fishermanBusiness,
        allFishermanBusinessCategory,
        allFishermanBusinessLocation,
        fishermanBusinessWebsitePage,
        announcementComponent,
        allSitePage,
        footerRegularPages,
        bookingUrl,
      } = data;

      return (
        <React.Fragment>
          <SiteNavigationElementSchema
            pages={allFishermanBusinessWebsitePage.nodes}
            siteUrl={"https://www.salonwaveokc.com"}
          />
          <LocalBusinessSchema
            name={fishermanBusiness.name}
            categories={allFishermanBusinessCategory.nodes}
            url={"https://www.salonwaveokc.com"}
            logo={fishermanBusiness.logo}
            address={allFishermanBusinessLocation.nodes}
            menu={fishermanBusiness.menu}
            images={getComponentFiles({
              components: fishermanBusinessWebsitePage.components,
              componentName: "HeroElement",
              numToSelect: 1,
            })}
          />
          <div className={"site-container"}>
            <React.Fragment>
              <RecaptchaProvider>
                <TimedElement
                  delay={announcementComponent.data.delay}
                  referenceId={announcementComponent.data.referenceId}
                  timezone={fishermanBusiness.primaryLocation.timezone}
                >
                  <TimedElement.Element>
                    <TimedElement.ActionComponent
                      passThroughActionProp={"onClose"}
                      action={"hide"}
                    >
                      <FishermanModal
                        className={"announcement-modal"}
                        dimmer={"inverted"}
                        defaultOpen={true}
                        closeIcon={
                          <Button icon={true}>
                            <FishermanIcon iconName={"close"} />
                          </Button>
                        }
                      >
                        <FishermanModal.Content>
                          <Announcement
                            images={createImages({
                              images: announcementComponent.files,
                            })}
                            contentNodes={announcementComponent.contentNodes}
                            interactions={announcementComponent.interactions}
                            imagePosition={
                              announcementComponent.data.imagePosition
                            }
                            businessId={fishermanBusiness._id}
                            businessType={fishermanBusiness.type}
                            locationId={fishermanBusiness.primaryLocation._id}
                          />
                        </FishermanModal.Content>
                      </FishermanModal>
                    </TimedElement.ActionComponent>
                  </TimedElement.Element>
                </TimedElement>
              </RecaptchaProvider>
              <ScrollElement
                scrollTarget={"#navigation"}
                targetPositionProperty={"bottom"}
                offset={360}
                className={"active"}
                applyOnlyToParent={true}
                scrollMode={"scrollPosition"}
                listenerNodeIds={["accordions-popup-menu-link"]}
              >
                <NavigationV2
                  id={"navigation"}
                  fullScreen={true}
                  desktop={
                    <React.Fragment>
                      <NavigationV2.Container
                        containerId={"desktop-navigation-bar"}
                      >
                        <NavigationV2.Menu>
                          <NavigationV2.Item>
                            <NavigationV2.Links
                              links={joinParentWithChildPages({
                                pages: filterRegularLinks({
                                  links: allFishermanBusinessWebsitePage.nodes,
                                }),
                                generatedPages: allSitePage.nodes,
                              })}
                              renderChildPages={
                                <PopupAccordionMenuLink
                                  targetPositionId={"desktop-navigation-bar"}
                                  navigationMode={"category"}
                                />
                              }
                            />
                          </NavigationV2.Item>
                        </NavigationV2.Menu>
                        <NavigationV2.Menu>
                          <NavigationV2.Logo
                            title={
                              <FishermanHeader>
                                {fishermanBusiness.name}
                              </FishermanHeader>
                            }
                            src={fishermanBusiness.gatsbyLogo.childImageSharp}
                          />
                        </NavigationV2.Menu>
                        <NavigationV2.Menu>
                          <NavigationV2.Links
                            links={filterButtonLinks({
                              links: allFishermanBusinessWebsitePage.nodes,
                            })}
                          />
                        </NavigationV2.Menu>
                      </NavigationV2.Container>
                    </React.Fragment>
                  }
                  mobile={
                    <React.Fragment>
                      <NavigationV2.Container className={"menu-header"}>
                        <NavigationV2.Logo
                          title={
                            <FishermanHeader>
                              {fishermanBusiness.name}
                            </FishermanHeader>
                          }
                          src={fishermanBusiness.gatsbyLogo.childImageSharp}
                        />
                        <NavigationV2.Toggle />
                      </NavigationV2.Container>
                      <NavigationV2.Container
                        tray={true}
                        vertical={true}
                        className={"menu-content"}
                      >
                        <div className={"menu-content-header"}>
                          <NavigationV2.Logo
                            title={
                              <FishermanHeader>
                                {fishermanBusiness.name}
                              </FishermanHeader>
                            }
                            src={fishermanBusiness.gatsbyLogo.childImageSharp}
                          />
                          <NavigationV2.Toggle />
                        </div>
                        <NavigationV2.Menu>
                          <div className={"button-links-container"}>
                            <NavigationV2.Links
                              links={filterButtonLinks({
                                links: allFishermanBusinessWebsitePage.nodes,
                              })}
                            />
                          </div>
                          <div className={"regular-links-container"}>
                            <NavigationV2.Links
                              links={joinParentWithChildPages({
                                pages: filterRegularLinks({
                                  links: allFishermanBusinessWebsitePage.nodes,
                                }),
                                generatedPages: allSitePage.nodes,
                              })}
                            />
                          </div>
                        </NavigationV2.Menu>
                        <div className={"actions-container"}>
                          <HookComponentProvider
                            hook={useContext}
                            component={
                              <Phone
                                content={"Call Us"}
                                iconName={"phone"}
                                event={{
                                  category: "Contact",
                                  action: "Call",
                                  label: "Navigation",
                                }}
                              />
                            }
                            hookToPropsMap={{
                              phone: "activeLocation.phoneNumber",
                            }}
                            hookParams={[LocationsContext]}
                            requiredProps={["phone"]}
                            onlyRenderOnClientSide={true}
                          />
                        </div>
                      </NavigationV2.Container>
                    </React.Fragment>
                  }
                />
              </ScrollElement>
            </React.Fragment>
            <RecaptchaProvider>
              <main className={"page-container"}>{children}</main>
              <footer>
                <Grid stackable={true} doubling={true}>
                  <Grid.Row className={"main-row"}>
                    <Grid.Column
                      className={"links-and-social-media"}
                      width={10}
                    >
                      <Grid.Row className={"external-links-row"}>
                        <Grid.Column className={"logo"}>
                          <NavigationV2.Logo
                            title={
                              <FishermanHeader>
                                {fishermanBusiness.name}
                              </FishermanHeader>
                            }
                            src={fishermanBusiness.gatsbyLogo.childImageSharp}
                          />
                        </Grid.Column>
                        <Grid.Column className={"links"}>
                          <NavigationV2.Links
                            links={filterButtonLinks({
                              links: allFishermanBusinessWebsitePage.nodes,
                            })}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row className={"sitemap-row"}>
                        <Grid.Column className={"sitemap"}>
                          <SiteMap
                            links={footerRegularPages.nodes}
                            horizontal={false}
                            bulleted={false}
                            className={"website"}
                          />
                          <SiteMap
                            links={getParentGeneratedChildPages({
                              pages: allFishermanBusinessWebsitePage.nodes,
                              tagName: "ServiceCategories",
                              parentPageType: "Services",
                              generatedPages: allSitePage.nodes,
                            })}
                            horizontal={false}
                            bulleted={false}
                            className={"services"}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row className={"social-media-row"}>
                        <Grid.Column className={"social-media"}>
                          <SocialMediaV2>
                            <MultipleComponentIterator
                              iterator={fishermanBusiness.socialMedia}
                              components={[
                                {
                                  component: <SocialMediaV2.Platform />,
                                  propMap: {
                                    key: "_id",
                                    url: "link",
                                    platform: "type",
                                  },
                                  children: [
                                    {
                                      component: <SocialMediaV2.Icon />,
                                      propMap: { icon: "type" },
                                    },
                                  ],
                                },
                              ]}
                            />
                          </SocialMediaV2>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid.Column>
                    <Grid.Column
                      className={"single-location contact-info"}
                      width={6}
                    >
                      <strong>Contact Us</strong>
                      <HookComponentProvider
                        hook={useContext}
                        component={
                          <Location
                            inline={true}
                            businessName={fishermanBusiness.name}
                            showBusinessName={false}
                          />
                        }
                        hookToPropsMap={{
                          "address.street": "activeLocation.street",
                          "address.city": "activeLocation.city",
                          "address.state": "activeLocation.state",
                          "address.zip": "activeLocation.zipCode",
                        }}
                        requiredProps={[["address"]]}
                        hookParams={[LocationsContext]}
                      />
                      <HookComponentProvider
                        hook={useContext}
                        component={Phone}
                        hookToPropsMap={{ phone: "activeLocation.phoneNumber" }}
                        hookParams={[LocationsContext]}
                        onlyRenderOnClientSide={true}
                      />
                      <HookComponentProvider
                        hook={useContext}
                        component={Email}
                        hookToPropsMap={{ email: "activeLocation.email" }}
                        hookParams={[LocationsContext]}
                        onlyRenderOnClientSide={true}
                      />
                      <HookComponentProvider
                        hook={useContext}
                        component={
                          <HoursOfOperation
                            displayOption={"dailyWithPopup"}
                            displayOpenClosedStatus={true}
                            enhancedLabels={{
                              openingSoon: "Opening soon",
                              open: "Open now",
                            }}
                          />
                        }
                        hookToPropsMap={{
                          hours: "activeLocation.hours",
                          timezone: "activeLocation.timezone",
                        }}
                        hookParams={[LocationsContext]}
                        onlyRenderOnClientSide={true}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className={"copyright-row"}>
                    <Grid.Column className={"copyright"}>
                      <CopyrightLabel
                        company={fishermanBusiness.name}
                        phrase={"All Rights Reserved"}
                        privacyPolicyLink={
                          <OutboundLink
                            to={
                              "https://fisherman-static.s3.amazonaws.com/public/__fisherman/Client+Website+Privacy+Policy.pdf"
                            }
                            label={"Privacy Policy"}
                            eventLabel={"copyright-privacy-policy"}
                          />
                        }
                        brand={fishermanBusiness.brandIdentity}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <FishermanBanner brand={fishermanBusiness.brandIdentity} />
              </footer>
            </RecaptchaProvider>
            <PageTransition />
            <div className={"bottom-bar-actions-container"}>
              <HookComponentProvider
                hook={useContext}
                component={
                  <Phone
                    iconName={"phone"}
                    event={{
                      category: "Contact",
                      action: "Call",
                      label: "Navigation",
                    }}
                  />
                }
                hookToPropsMap={{ phone: "activeLocation.phoneNumber" }}
                hookParams={[LocationsContext]}
                requiredProps={["phone"]}
                onlyRenderOnClientSide={true}
              />
              <Button
                as={OutboundLink}
                to={bookingUrl.url}
                event={{
                  category: "Booking",
                  action: "Schedule Appointment Intent",
                }}
                content={"Book Now"}
                aria-label={"Book an Appointment"}
              />
            </div>
          </div>
        </React.Fragment>
      );
    }}
  />
);
